import { unref } from 'vue'
import { useBookingStore } from '../../Registration/stores/bookingStore'
import { dataGet } from '../../Common/utils'

const LOGICAL_OPERATOR_AND = 'and'
const LOGICAL_OPERATOR_OR = 'or'
const RULE_TYPE_GLOBAL = 'global'
const RULE_TYPE_LOCAL = 'local'

export function useVisibilityRules(bookingData = null) {
  const getBookingData = () => {
    if (bookingData !== null) return unref(bookingData)
    const store = useBookingStore()
    return store.data
  }

  const evaluateVisibilityRules = (visibilityRules, companionIndex = null) => {
    // If no rules, field is visible
    if (!visibilityRules || Object.keys(visibilityRules).length === 0) {
      return true
    }

    const data = getBookingData()

    if (companionIndex !== null) {
      return evaluateForCompanion(visibilityRules, data, companionIndex)
    }

    return evaluateForGuest(visibilityRules, data)
  }

  const evaluateForGuest = (rules, rsvp, companionIndex = null) => {
    const permissions = rules

    switch (permissions.type) {
      case 'public':
        return true
      case 'private':
        return (
          rsvp !== null && rsvp !== undefined && Object.keys(rsvp).length > 0
        )
      case 'custom':
        return (
          rsvp !== null &&
          rsvp !== undefined &&
          permissions.query !== null &&
          evaluateGroup(permissions.query, rsvp, companionIndex)
        )
      default:
        return true
    }
  }

  const evaluateForCompanion = (rules, rsvp, companionIndex) => {
    return evaluateForGuest(rules, rsvp, companionIndex)
  }

  const evaluateGroup = (group, rsvp, companionIndex = null) => {
    const logicalOperator = group.logicalOperator
    const items = group.items
    let result = logicalOperator === LOGICAL_OPERATOR_AND

    for (const item of items) {
      if (item.type === 'item') {
        if (logicalOperator === LOGICAL_OPERATOR_AND) {
          result = result && evaluateItem(item, rsvp, companionIndex)
        } else if (logicalOperator === LOGICAL_OPERATOR_OR) {
          result = result || evaluateItem(item, rsvp, companionIndex)
        }
      } else if (item.type === 'group') {
        if (logicalOperator === LOGICAL_OPERATOR_AND) {
          result = result && evaluateGroup(item, rsvp, companionIndex)
        } else if (logicalOperator === LOGICAL_OPERATOR_OR) {
          result = result || evaluateGroup(item, rsvp, companionIndex)
        }
      }
    }

    return result
  }

  const checkIsLoggedInRule = (item) => {
    const shouldBeLoggedIn = Boolean(item.matching_value || false)
    const data = getBookingData()
    const code = data.code || null
    return (code === null) !== shouldBeLoggedIn
  }

  const evaluateItem = (item, rsvp, companionIndex = null) => {
    const operand = item.operand

    // Special condition for Authentication
    if (operand === 'is_logged_in') {
      return checkIsLoggedInRule(item)
    }

    const ruleType = item.rule_type || RULE_TYPE_GLOBAL
    let actualOperand = item.operand

    if (ruleType === RULE_TYPE_LOCAL && companionIndex !== null) {
      actualOperand = `companions.${companionIndex}.${item.operand}`
    }

    let value = dataGet(rsvp, actualOperand)
    const dataType = item.data_type || 'string'
    let matchingValue = item.matching_value

    if (dataType === 'boolean') {
      value = Boolean(value)
      matchingValue = Boolean(matchingValue)
    }

    if (dataType === 'multiselect_enum') {
      const arrayValue = Array.isArray(value) ? value : []
      value = arrayValue.includes(matchingValue)
      matchingValue = Boolean(matchingValue)
    }

    return getEvaluatorForOperator(item.operator)(value, matchingValue)
  }

  const getEvaluatorForOperator = (operator) => {
    switch (operator) {
      case 'empty':
        return (operand) => [null, '', [], false, 0].includes(operand)
      case 'not_empty':
        return (operand) => ![null, '', [], false, 0].includes(operand)
      case 'not_equal':
        return (operand, value) => operand !== value
      case 'equal':
      default:
        return (operand, value) => operand === value
    }
  }

  return {
    evaluateVisibilityRules,
    evaluateForGuest,
    evaluateForCompanion,
    evaluateGroup,
    evaluateItem,
    checkIsLoggedInRule,
    getEvaluatorForOperator,
  }
}
